import { supabase } from './supabase';

/**
 * Paiement en ligne côté CLIENT (parité C1). S'appuie sur l'Edge Function existante
 * `create-payment` (routeur multi-fournisseurs : Stripe / SwissPayout / Adyen),
 * qui renvoie soit un `client_secret` (Stripe Payment Element), soit une `payment_url`
 * (redirection). Le PSP, les clés et le webhook existent déjà côté Supabase ;
 * il manquait juste l'appel depuis la vitrine React.
 *
 * Helpers purs (payload, URL de retour) testables séparément de l'I/O.
 */

export interface PaymentRequest {
  tenantId: string;
  amountCents: number;
  orderNumber: string;
  successUrl: string;
  failedUrl?: string;
  cancelUrl?: string;
}

export type PaymentResult =
  | { mode: 'redirect'; provider: string; paymentUrl: string }
  | { mode: 'client_secret'; provider: string; clientSecret: string }
  | { mode: 'error'; error: string };

/** URL de retour après paiement (page « merci »), parité legacy `/merci`. Pur. */
export function successUrlFor(origin: string, slug: string, orderNumber: string): string {
  const base = origin.replace(/\/$/, '');
  return `${base}/r/${slug}/merci?order=${encodeURIComponent(orderNumber)}`;
}

/** Corps envoyé à l'Edge Function `create-payment`. Pur. */
export function buildPaymentBody(req: PaymentRequest): Record<string, unknown> {
  return {
    tenant_id: req.tenantId,
    amount_cents: req.amountCents,
    order_number: req.orderNumber,
    success_url: req.successUrl,
    failed_url: req.failedUrl ?? req.successUrl,
    cancel_url: req.cancelUrl ?? req.successUrl,
  };
}

/** Normalise la réponse de l'Edge Function en un résultat typé. Pur. */
export function parsePaymentResponse(data: unknown): PaymentResult {
  const d = (data ?? {}) as Record<string, unknown>;
  if (typeof d.payment_url === 'string' && d.payment_url) {
    return { mode: 'redirect', provider: String(d.provider ?? 'redirect'), paymentUrl: d.payment_url };
  }
  if (typeof d.client_secret === 'string' && d.client_secret) {
    return { mode: 'client_secret', provider: String(d.provider ?? 'stripe'), clientSecret: d.client_secret };
  }
  return { mode: 'error', error: String(d.error ?? d.message ?? 'Réponse de paiement invalide') };
}

/**
 * Lance la création du paiement en ligne.
 * 1) Tente `stripe-checkout-session` (Stripe Checkout HÉBERGÉ → redirection, sans
 *    dépendance Stripe.js côté client). 2) Repli sur `create-payment` (routeur
 *    multi-fournisseurs existant) si la 1re n'est pas déployée.
 * Renvoie un résultat typé (redirect / client_secret / error).
 */
export async function requestPayment(req: PaymentRequest): Promise<PaymentResult> {
  const body = buildPaymentBody(req);
  // 1) Stripe Checkout hébergé (redirection).
  try {
    const { data, error } = await supabase.functions.invoke('stripe-checkout-session', { body });
    if (!error) {
      const res = parsePaymentResponse(data);
      if (res.mode !== 'error') return res;
    }
  } catch {
    /* fonction non déployée → on tente le routeur générique */
  }
  // 2) Repli : routeur multi-fournisseurs existant.
  try {
    const { data, error } = await supabase.functions.invoke('create-payment', { body });
    if (error) return { mode: 'error', error: error.message };
    return parsePaymentResponse(data);
  } catch (e) {
    return { mode: 'error', error: e instanceof Error ? e.message : 'Échec du paiement' };
  }
}
